/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Briefcase } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import { JobTagChips } from '@/components/dispatch/JobTagChips';
import { DataTableFilterSection } from '@/components/organisms/DataTableFilterSection';
import { EntityListCompactItem, EntitySplitListPanel } from '@/components/organisms';
import {
  DataTableDateRangeField,
  DataTableNumberFilterField,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  EntityListActiveDatePill,
  EntityListAdvancedFiltersSheet,
  EntityListCountFooter,
  type DataTableNumberOperator,
} from '@/components/molecules';
import { useJobs } from '@/hooks/jobs/useJobs';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useActiveJobTypes } from '@/hooks/settings/useJobTypes';
import { useListPanelSearch } from '@/hooks/lib/useListPanelState';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { jobPriceDisplay } from '@/components/organisms/jobs/recurring/recurringDetail.helpers';
import type {
  Job,
  JobListParams,
  JobPriority,
  JobStatus,
  JobTypeOnJob,
  RecurringStatus,
} from '@/types/job';
import { JobRowMenu } from './JobRowMenu';

function fmt(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatJobType(type: string | JobTypeOnJob | undefined) {
  const name = typeof type === 'string' ? type : type?.name;
  if (!name) return 'Type not set';
  return name
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatRailDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMM d, h:mm a');
}

function effectiveJobStatus(job: Job): string {
  return job.recurringStatus ?? job.status;
}

type SortOption = {
  label: string;
  // 'listPrice' = billing-mode-aware Total (BE generated column); matches the display.
  sortBy: 'createdAt' | 'updatedAt' | 'listPrice' | 'jobNumber';
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

export type JobStatusFilter =
  | JobStatus
  | Extract<RecurringStatus, 'PAST_DUE' | 'SUSPENDED'>
  | 'all';
type JobPriorityFilter = JobPriority | 'all';

export interface JobListFilters {
  q: string;
  status: JobStatusFilter;
  jobTypeId: string;
  // NOT-542: business-unit filter ('all' = no filter).
  businessUnitId: string;
  priority: JobPriorityFilter;
  assignedTo: string;
  grandTotalOp: DataTableNumberOperator;
  grandTotalValue: string;
  page: number;
  sort: number;
  dateFrom: string | undefined;
  dateTo: string | undefined;
  renewalDue: boolean;
  // NOT-560 — limit the list to service calls (backend isServiceCall=true).
  serviceCall: boolean;
  hotspotPostalArea?: string;
  hotspotJobTypeId?: string;
  hotspotTechnicianId?: string;
  hotspotCountry?: 'US' | 'CA';
  hotspotRegionCode?: string;
}

export const DEFAULT_JOB_LIST_FILTERS: JobListFilters = {
  q: '',
  status: 'all',
  jobTypeId: 'all',
  businessUnitId: 'all',
  priority: 'all',
  assignedTo: '',
  grandTotalOp: 'eq',
  grandTotalValue: '',
  page: 1,
  sort: 0,
  dateFrom: undefined,
  dateTo: undefined,
  renewalDue: false,
  serviceCall: false,
};

export function resolveJobListFilters(search: Partial<JobListFilters>): JobListFilters {
  return {
    q: search.q ?? DEFAULT_JOB_LIST_FILTERS.q,
    status: search.status ?? DEFAULT_JOB_LIST_FILTERS.status,
    jobTypeId: search.jobTypeId ?? DEFAULT_JOB_LIST_FILTERS.jobTypeId,
    businessUnitId: search.businessUnitId ?? DEFAULT_JOB_LIST_FILTERS.businessUnitId,
    priority: search.priority ?? DEFAULT_JOB_LIST_FILTERS.priority,
    assignedTo: search.assignedTo ?? DEFAULT_JOB_LIST_FILTERS.assignedTo,
    grandTotalOp: search.grandTotalOp ?? DEFAULT_JOB_LIST_FILTERS.grandTotalOp,
    grandTotalValue: search.grandTotalValue ?? DEFAULT_JOB_LIST_FILTERS.grandTotalValue,
    page: search.page ?? DEFAULT_JOB_LIST_FILTERS.page,
    sort: search.sort ?? DEFAULT_JOB_LIST_FILTERS.sort,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    renewalDue: search.renewalDue ?? DEFAULT_JOB_LIST_FILTERS.renewalDue,
    serviceCall: search.serviceCall ?? DEFAULT_JOB_LIST_FILTERS.serviceCall,
    hotspotPostalArea: search.hotspotPostalArea,
    hotspotJobTypeId: search.hotspotJobTypeId,
    hotspotTechnicianId: search.hotspotTechnicianId,
    hotspotCountry: search.hotspotCountry,
    hotspotRegionCode: search.hotspotRegionCode,
  };
}

export const JOB_STATUS_FILTERS: Array<{ value: JobStatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'PAST_DUE', label: 'Past due' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'INVOICED', label: 'Invoiced' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const JOB_DOT_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-400',
  SCHEDULED: 'bg-blue-500',
  IN_PROGRESS: 'bg-emerald-500',
  ON_HOLD: 'bg-yellow-500',
  COMPLETED: 'bg-green-500',
  INVOICED: 'bg-teal-500',
  CLOSED: 'bg-slate-500',
  CANCELLED: 'bg-red-500',
  ARCHIVED: 'bg-gray-500',
  PAST_DUE: 'bg-amber-500',
  SUSPENDED: 'bg-red-500',
};

const ALL_JOB_TYPES_OPTION = { value: 'all', label: 'All types' };

const JOB_PRIORITY_FILTERS: Array<{ value: JobPriorityFilter; label: string }> = [
  { value: 'all', label: 'All priorities' },
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

export const JOB_SORT_OPTIONS: SortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Updated', sortBy: 'updatedAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Total', sortBy: 'listPrice', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Total', sortBy: 'listPrice', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Job #', sortBy: 'jobNumber', sortDir: 'desc', icon: <ArrowDown size={12} /> },
];

interface JobListPanelProps {
  selectedId?: string;
  onSelect: (id: string) => void;
  compact?: boolean;
  filters: JobListFilters;
  onFiltersChange: (patch: Partial<JobListFilters>) => void;
  /** Operational Service calls lens: the capability filter cannot be cleared. */
  serviceCallOnly?: boolean;
  hotspotPostalArea?: string;
  hotspotJobTypeId?: string;
  hotspotTechnicianId?: string;
  hotspotCountry?: 'US' | 'CA';
  hotspotRegionCode?: string;
}

const PAGE_SIZE = 20;

export function JobListPanel({
  selectedId,
  onSelect,
  filters,
  onFiltersChange,
  serviceCallOnly = false,
  hotspotPostalArea,
  hotspotJobTypeId,
  hotspotTechnicianId,
  hotspotCountry,
  hotspotRegionCode,
}: JobListPanelProps) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // NOT-542: only offer the Business Unit filter to tenants that use them.
  const { data: businessUnits = [] } = useBusinessUnits('active');
  const hasBusinessUnits = businessUnits.length > 0;
  const businessUnitFilterOptions = [
    { value: 'all', label: 'All business units' },
    ...businessUnits.map((unit) => ({ value: unit.id, label: unit.name })),
  ];
  // The tenant's configured job types — this filter used to offer a hardcoded
  // legacy enum that no configured tenant could ever match.
  const { data: activeJobTypes = [] } = useActiveJobTypes();
  const jobTypeFilterOptions = [
    ALL_JOB_TYPES_OPTION,
    ...activeJobTypes.map((jobType) => ({ value: jobType.id, label: jobType.name })),
  ];
  const {
    searchInput: search,
    setSearchInput: setSearch,
    debouncedSearch,
  } = useListPanelSearch({
    value: filters.q,
    onChange: (value) => onFiltersChange({ q: value, page: 1 }),
  });
  const availableSortOptions = pricingHidden
    ? JOB_SORT_OPTIONS.filter((option) => option.sortBy !== 'listPrice')
    : JOB_SORT_OPTIONS;
  const requestedSort = JOB_SORT_OPTIONS[filters.sort] ?? JOB_SORT_OPTIONS[0];
  const activeSort = availableSortOptions.includes(requestedSort)
    ? requestedSort
    : availableSortOptions[0];
  const activeSortIndex = availableSortOptions.indexOf(activeSort);

  const dateRange: DateRange | undefined =
    filters.dateFrom || filters.dateTo
      ? {
          from: filters.dateFrom ? parseISO(filters.dateFrom) : undefined,
          to: filters.dateTo ? parseISO(filters.dateTo) : undefined,
        }
      : undefined;
  const [dateRangeDraft, setDateRangeDraft] = useState<DateRange | undefined>(dateRange);

  useEffect(() => {
    setDateRangeDraft(
      filters.dateFrom || filters.dateTo
        ? {
            from: filters.dateFrom ? parseISO(filters.dateFrom) : undefined,
            to: filters.dateTo ? parseISO(filters.dateTo) : undefined,
          }
        : undefined,
    );
  }, [filters.dateFrom, filters.dateTo]);

  const params: JobListParams = {
    page: filters.page,
    limit: PAGE_SIZE,
    q: debouncedSearch || undefined,
    hotspotPostalArea,
    hotspotJobTypeId,
    hotspotTechnicianId,
    hotspotCountry,
    hotspotRegionCode,
    hotspotFrom: hotspotPostalArea ? filters.dateFrom : undefined,
    hotspotTo: hotspotPostalArea ? filters.dateTo : undefined,
    status:
      filters.status !== 'all' && filters.status !== 'PAST_DUE' && filters.status !== 'SUSPENDED'
        ? filters.status
        : undefined,
    recurringStatus:
      filters.status === 'PAST_DUE' || filters.status === 'SUSPENDED' ? filters.status : undefined,
    jobTypeId: filters.jobTypeId !== 'all' ? filters.jobTypeId : undefined,
    businessUnitId:
      hasBusinessUnits && filters.businessUnitId !== 'all' ? filters.businessUnitId : undefined,
    priority: filters.priority !== 'all' ? filters.priority : undefined,
    assignedTo: filters.assignedTo.trim() || undefined,
    dateFrom: hotspotPostalArea ? undefined : filters.dateFrom,
    dateTo: hotspotPostalArea ? undefined : filters.dateTo,
    renewalDue: filters.renewalDue || undefined,
    isServiceCall: serviceCallOnly || filters.serviceCall || undefined,
    sortBy: activeSort.sortBy,
    sortDir: activeSort.sortDir,
  };

  // Total filter targets the billing-mode-aware listPrice (BE generated column) so
  // it matches the displayed/sorted amount for recurring SUBSCRIPTION/PREPAID jobs.
  const grandTotalValue = Number(filters.grandTotalValue);
  if (!pricingHidden && filters.grandTotalValue.trim() && Number.isFinite(grandTotalValue)) {
    if (filters.grandTotalOp === 'eq') params.listPriceEq = grandTotalValue;
    if (filters.grandTotalOp === 'gt' || filters.grandTotalOp === 'gte') {
      params.listPriceMin = grandTotalValue;
    }
    if (filters.grandTotalOp === 'lt' || filters.grandTotalOp === 'lte') {
      params.listPriceMax = grandTotalValue;
    }
  }

  const query = useJobs(params);
  const jobs = query.data?.data ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, query.data?.totalPages ?? Math.ceil(total / PAGE_SIZE));

  function clearDate() {
    setDateRangeDraft(undefined);
    onFiltersChange({ dateFrom: undefined, dateTo: undefined, page: 1 });
  }

  const activeAdvancedCount =
    (filters.status !== 'all' ? 1 : 0) +
    (filters.jobTypeId !== 'all' ? 1 : 0) +
    (hasBusinessUnits && filters.businessUnitId !== 'all' ? 1 : 0) +
    (filters.priority !== 'all' ? 1 : 0) +
    (filters.assignedTo.trim() ? 1 : 0) +
    (!pricingHidden && filters.grandTotalValue.trim() ? 1 : 0) +
    (dateRange?.from || dateRange?.to ? 1 : 0) +
    (filters.renewalDue ? 1 : 0) +
    (!serviceCallOnly && filters.serviceCall ? 1 : 0);

  function resetAdvancedFilters() {
    setDateRangeDraft(undefined);
    onFiltersChange({
      status: 'all',
      jobTypeId: 'all',
      businessUnitId: 'all',
      priority: 'all',
      assignedTo: '',
      grandTotalOp: 'eq',
      grandTotalValue: '',
      dateFrom: undefined,
      dateTo: undefined,
      renewalDue: false,
      serviceCall: serviceCallOnly,
      page: 1,
    });
  }

  const filterSheet = (
    <EntityListAdvancedFiltersSheet
      open={filtersOpen}
      onOpenChange={setFiltersOpen}
      activeCount={activeAdvancedCount}
      onReset={resetAdvancedFilters}
      resetDisabled={activeAdvancedCount === 0}
    >
      <DataTableFilterSection
        label="Status"
        active={filters.status !== 'all'}
        onClear={() => onFiltersChange({ status: 'all', page: 1 })}
      >
        <DataTableSelectFilterField
          value={filters.status}
          onValueChange={(value) => onFiltersChange({ status: value as JobStatusFilter, page: 1 })}
          placeholder="All statuses"
          options={JOB_STATUS_FILTERS.map((filter) => ({
            value: filter.value,
            label: filter.value === 'all' ? 'All statuses' : filter.label,
          }))}
        />
      </DataTableFilterSection>

      <DataTableFilterSection
        label="Renewal"
        active={filters.renewalDue}
        onClear={() => onFiltersChange({ renewalDue: false, page: 1 })}
      >
        <DataTableSelectFilterField
          value={filters.renewalDue ? 'due' : 'all'}
          onValueChange={(value) => onFiltersChange({ renewalDue: value === 'due', page: 1 })}
          placeholder="All jobs"
          options={[
            { value: 'all', label: 'All jobs' },
            { value: 'due', label: 'Renewal due' },
          ]}
        />
      </DataTableFilterSection>

      {/* NOT-560 — filters on the isServiceCall capability flag, not `source`. */}
      {!serviceCallOnly ? (
        <DataTableFilterSection
          label="Service call"
          active={filters.serviceCall}
          onClear={() => onFiltersChange({ serviceCall: false, page: 1 })}
        >
          <DataTableSelectFilterField
            value={filters.serviceCall ? 'service-call' : 'all'}
            onValueChange={(value) =>
              onFiltersChange({ serviceCall: value === 'service-call', page: 1 })
            }
            placeholder="All jobs"
            options={[
              { value: 'all', label: 'All jobs' },
              { value: 'service-call', label: 'Service calls only' },
            ]}
          />
        </DataTableFilterSection>
      ) : null}

      <DataTableFilterSection
        label="Job Type"
        active={filters.jobTypeId !== 'all'}
        onClear={() => onFiltersChange({ jobTypeId: 'all', page: 1 })}
      >
        <DataTableSelectFilterField
          value={filters.jobTypeId}
          onValueChange={(value) => onFiltersChange({ jobTypeId: value, page: 1 })}
          placeholder="All types"
          options={jobTypeFilterOptions}
        />
      </DataTableFilterSection>

      {hasBusinessUnits ? (
        <DataTableFilterSection
          label="Business Unit"
          active={filters.businessUnitId !== 'all'}
          onClear={() => onFiltersChange({ businessUnitId: 'all', page: 1 })}
        >
          <DataTableSelectFilterField
            value={filters.businessUnitId}
            onValueChange={(value) => onFiltersChange({ businessUnitId: value, page: 1 })}
            placeholder="All business units"
            options={businessUnitFilterOptions}
          />
        </DataTableFilterSection>
      ) : null}

      <DataTableFilterSection
        label="Priority"
        active={filters.priority !== 'all'}
        onClear={() => onFiltersChange({ priority: 'all', page: 1 })}
      >
        <DataTableSelectFilterField
          value={filters.priority}
          onValueChange={(value) =>
            onFiltersChange({ priority: value as JobPriorityFilter, page: 1 })
          }
          placeholder="All priorities"
          options={JOB_PRIORITY_FILTERS}
        />
      </DataTableFilterSection>

      <DataTableFilterSection
        label="Job owner"
        active={!!filters.assignedTo.trim()}
        onClear={() => onFiltersChange({ assignedTo: '', page: 1 })}
      >
        <DataTableSearchFilterField
          value={filters.assignedTo}
          onValueChange={(value) => onFiltersChange({ assignedTo: value, page: 1 })}
          placeholder="Name or ID..."
        />
      </DataTableFilterSection>

      {!pricingHidden ? (
        <DataTableFilterSection
          label="Total"
          active={!!filters.grandTotalValue.trim()}
          onClear={() => onFiltersChange({ grandTotalOp: 'eq', grandTotalValue: '', page: 1 })}
        >
          <DataTableNumberFilterField
            op={filters.grandTotalOp}
            value={filters.grandTotalValue}
            onOpChange={(op) => onFiltersChange({ grandTotalOp: op, page: 1 })}
            onValueChange={(value) => onFiltersChange({ grandTotalValue: value, page: 1 })}
            placeholder="0.00"
          />
        </DataTableFilterSection>
      ) : null}

      <DataTableFilterSection
        label="Created At"
        active={!!(dateRange?.from || dateRange?.to)}
        onClear={clearDate}
      >
        <DataTableDateRangeField
          range={dateRangeDraft}
          onSelect={(range) => {
            setDateRangeDraft(range);
            if (range?.from && range.to) {
              onFiltersChange({
                dateFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                dateTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                page: 1,
              });
            } else if (!range?.from && !range?.to) {
              onFiltersChange({ dateFrom: undefined, dateTo: undefined, page: 1 });
            }
          }}
        />
      </DataTableFilterSection>
    </EntityListAdvancedFiltersSheet>
  );

  return (
    <EntitySplitListPanel
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search jobs...',
      }}
      statusFilter={{
        value: filters.status,
        options: JOB_STATUS_FILTERS,
        dotColors: JOB_DOT_COLORS,
        onChange: (value) => onFiltersChange({ status: value as JobStatusFilter, page: 1 }),
      }}
      sort={{
        activeIndex: activeSortIndex,
        activeLabel: activeSort.label,
        options: availableSortOptions,
        onSelect: (sort) =>
          onFiltersChange({
            sort: JOB_SORT_OPTIONS.indexOf(availableSortOptions[sort] ?? availableSortOptions[0]),
            page: 1,
          }),
        onReset: () =>
          onFiltersChange({
            sort: JOB_SORT_OPTIONS.indexOf(availableSortOptions[0]),
            page: 1,
          }),
      }}
      toolbarEnd={filterSheet}
      belowToolbar={<EntityListActiveDatePill range={dateRange} onClear={clearDate} />}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      errorTitle="Failed to load jobs"
      emptyIcon={Briefcase}
      emptyTitle="No jobs found"
      hasItems={jobs.length > 0}
      pagination={
        !query.isLoading && total > PAGE_SIZE
          ? {
              page: filters.page,
              totalPages,
              total,
              onPrevious: () => onFiltersChange({ page: filters.page - 1 }),
              onNext: () => onFiltersChange({ page: filters.page + 1 }),
            }
          : undefined
      }
      footer={
        !query.isLoading && total <= PAGE_SIZE ? (
          <EntityListCountFooter count={total} singular="job" />
        ) : null
      }
    >
      {jobs.map((job: Job) => {
        const isSelected = selectedId === job.id;
        const railDate = formatRailDate(job.scheduledStart ?? job.createdAt);
        const jobType = formatJobType(job.jobType);
        // Billing-mode-aware Total: recurring SUBSCRIPTION/PREPAID jobs show their
        // period/term price ($100 /mo), one-offs show grandTotal — matching the
        // list sort/filter (BE listPrice) and the detail header.
        const price = jobPriceDisplay(job);
        const total = price.amount > 0 ? `${fmt(price.amount)}${price.unit}` : null;
        const actions = (
          <JobRowMenu
            id={job.id}
            status={job.status}
            statusPhase={job.statusPhase}
            estimateId={job.estimateId}
            source={job.source}
            bookingType={job.bookingType}
            recurringStatus={job.recurringStatus}
            lineItems={job.lineItems}
          />
        );

        return (
          <EntityListCompactItem
            key={job.id}
            selected={isSelected}
            onClick={() => onSelect(job.id)}
            content={
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-1.5">
                  <p className="truncate text-sm font-medium">{job.name || 'Untitled job'}</p>
                  <div className="flex shrink-0 items-center gap-1">
                    {job.renewalDue ? (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                        Renewal due
                      </span>
                    ) : null}
                    <StatusBadge status={effectiveJobStatus(job)} />
                  </div>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span className="font-mono text-xs text-muted-foreground">{job.jobNumber}</span>
                  {railDate ? <span className="text-xs text-muted-foreground">·</span> : null}
                  {railDate ? (
                    <span className="text-xs text-muted-foreground">{railDate}</span>
                  ) : null}
                  {!pricingHidden && total ? (
                    <span className="ml-auto text-xs font-semibold">{total}</span>
                  ) : null}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <p className="min-w-0 truncate text-[11px] text-muted-foreground">{jobType}</p>
                  <JobTagChips tags={job.tags} max={3} />
                </div>
              </div>
            }
            actions={actions}
          />
        );
      })}
    </EntitySplitListPanel>
  );
}
